import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface NavigationContextType {
  isRecordingMode: boolean;
  setRecordingMode: (value: boolean) => void;
  lastActiveStoryId: string | null;
  setLastActiveStoryId: (id: string | null) => void;
  showStoryPicker: boolean;
  setShowStoryPicker: (value: boolean) => void;
  currentRoute: string;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [isRecordingMode, setIsRecordingMode] = useState(false);
  const [lastActiveStoryId, setLastActiveStoryIdState] = useState<string | null>(() => {
    return localStorage.getItem('lastActiveStoryId');
  });
  const [showStoryPicker, setShowStoryPicker] = useState(false);

  const setLastActiveStoryId = useCallback((id: string | null) => {
    setLastActiveStoryIdState(id);
    if (id) {
      localStorage.setItem('lastActiveStoryId', id);
    } else {
      localStorage.removeItem('lastActiveStoryId');
    }
  }, []);

  const setRecordingMode = useCallback((value: boolean) => {
    setIsRecordingMode(value);
  }, []);

  useEffect(() => {
    const storyMatch = location.pathname.match(/\/story-recorder\/([^/]+)/);
    if (storyMatch) {
      setLastActiveStoryId(storyMatch[1]);
    }
  }, [location.pathname, setLastActiveStoryId]);

  return (
    <NavigationContext.Provider
      value={{
        isRecordingMode,
        setRecordingMode,
        lastActiveStoryId,
        setLastActiveStoryId,
        showStoryPicker,
        setShowStoryPicker,
        currentRoute: location.pathname
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
